
import {
  Component,
  Mixins,
  Prop,
  Provide,
  Watch
} from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { QUERY_PARAMS } from "@/data/constants";

// Types
import { IProvider } from "@/models/providers";
import {
  IProvisionProviderMethods,
  IProductProvisionProviderData
} from "@/models/providers/provisioning";
import { IProviderMethods } from "@/models/providers";
import { IBasket, IBasketProduct } from "@/models/baskets";
import { IProduct } from "@/models/products";
import { IBasketProvisioningFieldsForm } from "@/models/provisioning";
import { IClient } from "@/models/clients";

@Component
export default class ProductProvisionProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public basketId!: IBasket["id"];

  @Prop({ required: true })
  public basketProduct!: IBasketProduct;

  @Prop({ required: true })
  public product!: IProduct;

  @Prop({ required: true })
  public subProductIds!: IProduct["id"][];

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ required: true })
  public provisionMethods!: IProvisionProviderMethods;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ required: true })
  public initialProductForm!: boolean;

  // Data
  public isLoading = false;
  public isReloading = false;
  public isUpdating = false;
  public hasRequestError = false;

  public savedFormValues = {};
  public provisionFieldValues: IBasketProvisioningFieldsForm["provision_field_values"] = {};

  // Lifecycle methods

  public created() {
    if (!this.hasProvisioning) return;
    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    if (this.hasProvisioning) {
      if (this.basketProductId) {
        this.provisionMethods.destroyBasketProductFields(
          this.basketId,
          this.basketProductId
        );
        this.provisionMethods.destroyFieldsValues(
          this.basketId,
          this.basketProductId
        );
      }
      if (this.initialProductForm) {
        this.provisionMethods.destroyProductFields(this.productId);
      }
    }
  }

  // Methods

  public async loadData() {
    try {
      await this.loadProvisioning();
      this.initialiseForm();

      this.hasRequestError = false;
    } catch (error) {
      this.$emit("handleError", error); // Handle error by a parent
      this.$store.dispatch("api/handleError", error);

      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async loadProvisioning() {
    if (!this.basketProductId) {
      return this.provisionMethods.getProductFields(
        this.productId,
        this.clientId,
        this.subProductIds
      );
    }

    return Promise.all([
      this.provisionMethods.getBasketProductFields(
        this.basketId,
        this.basketProductId,
        this.subProductIds,
        true
      ),
      this.provisionMethods.getFieldsValues(
        this.basketId,
        this.basketProductId,
        this.subProductIds,
        true
      )
    ]);
  }

  public initialiseForm() {
    if (!this.basketProductId) {
      this.$emit("initialiseForm");
      _.forEach(this.productFields, (field: any) => {
        this.$set(
          this.provisionFieldValues,
          field.name,
          _.get(
            this.pathFieldValues,
            field.name,
            _.get(field, "default_value", "")
          )
        );
      });
      this.$emit("updateForm", this.provisionFieldValues);
      return;
    }

    _.forEach(this.productFields, (field: any) => {
      this.$set(
        this.provisionFieldValues,
        `${field.name}`,
        _.get(
          this.productFieldsValues,
          field.name,
          _.isEmpty(this.productFieldsValues)
            ? _.isNull(field.default_value)
              ? ""
              : field.default_value
            : ""
        )
      );
    });

    this.$emit("updateForm", this.provisionFieldValues);
    this.$emit("initialiseForm");
  }

  public updateProvisioningForm() {
    // Save and remove values from previous configuration
    _.forEach(_.keys(this.provisionFieldValues), (fieldKey: string) => {
      if (_.some(this.productFields, ["name", fieldKey])) return;

      this.$set(
        this.savedFormValues,
        fieldKey,
        this.provisionFieldValues[fieldKey]
      );

      delete this.provisionFieldValues[fieldKey];
    });

    _.forEach(this.productFields, (field: any) => {
      // If value was already touched, leave it
      if (_.has(this.provisionFieldValues, field.name)) return;

      // if value was removed from a previous configuration, bring it back
      if (_.has(this.savedFormValues, field.name)) {
        this.$set(
          this.provisionFieldValues,
          field.name,
          _.get(this.savedFormValues, field.name)
        );
        return;
      }

      // If not basket product and field was not touched before, use field value from the path or default value
      if (!this.basketProductId) {
        this.$set(
          this.provisionFieldValues,
          field.name,
          _.get(
            this.pathFieldValues,
            field.name,
            _.get(field, "default_value", "")
          )
        );
        return;
      }

      // If basket product configuration is changed
      if (_.isEmpty(this.savedFormValues)) {
        if (_.has(this.productFieldsValues, field.name)) {
          this.$set(
            this.provisionFieldValues,
            `${field.name}`,
            _.get(this.productFieldsValues, field.name)
          );
        }
        return;
      }

      // If basket product configuration is not changed
      this.$set(
        this.provisionFieldValues,
        `${field.name}`,
        _.get(field, "default_value", "")
      );
    });

    this.$emit("updateForm", this.provisionFieldValues);
  }

  // Computed

  public get basketProductId() {
    return _.get(this.basketProduct, "id", null);
  }

  public get productId() {
    return _.get(this.product, "id", "");
  }

  public get productFields() {
    return this.basketProductId && !this.initialProductForm
      ? this.provisionMethods.basketProductFields(
          this.basketId,
          this.basketProductId
        )
      : this.provisionMethods.productFields(this.productId);
  }

  public get productFieldsValues() {
    return this.basketProductId && !this.initialProductForm
      ? this.provisionMethods.basketProductFieldsValues(
          this.basketId,
          this.basketProductId
        )
      : [];
  }

  public get hasProvisioning() {
    return (
      (this.basketProductId &&
        !!_.get(this.basketProduct, "product.provision_blueprint_id")) ||
      !!_.get(this.product, "provision_blueprint_id")
    );
  }

  public get pathFieldValues() {
    const pathFieldValues = {};

    _.forEach(_.get(this.$route, "query", {}), (queryValue, queryKey) => {
      if (queryKey.includes(QUERY_PARAMS.PRODUCT_FIELDS)) {
        pathFieldValues[
          `${queryKey
            .replace(`${QUERY_PARAMS.PRODUCT_FIELDS}[`, "")
            .replace("]", "")}`
        ] = queryValue;
      }
    });

    return pathFieldValues;
  }

  // Watchers

  @Watch("subProductIds")
  public async refreshProvisionFields() {
    try {
      this.isUpdating = true;
      await this.loadProvisioning();
      this.updateProvisioningForm();

      this.hasRequestError = false;
    } catch (error) {
      this.$emit("handleError", error); // Handle error by a parent
      this.$store.dispatch("api/handleError", error);

      this.hasRequestError = true;
    } finally {
      this.isUpdating = false;
      this.isReloading = false;
    }
  }

  // Provide

  @Provide("productProvisionData")
  public providerData(): IProductProvisionProviderData {
    return {
      productFields: this.productFields,
      isUpdating: this.isUpdating,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError
    };
  }

  @Provide("productProvisionMethods")
  public get providerMethods(): IProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $productProvisionData: this.providerData(),
        $productProvisionMethods: this.providerMethods
      })
    );
  }
}
